import { BlockedButton } from "components/BlockedButton/BlockedButton";
import { Button, ButtonProps } from "components/DesignSystem/Button/Button";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { CaretDown } from "components/icons/CaretDown";
import { PermissionBasedUI } from "components/PermissionBasedUI/PermissionBasedUI";
import { RECONNECT_PLAID_ACCOUNT_SUCCESS } from "constants/analyticsEvents";
import { RECONNECT_BANK_ACCOUNT } from "constants/subscriptionPermissionFeatures";
import { useAnalytics } from "hooks/useAnalytics";
import { useBankConnect } from "hooks/useBankConnect";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useHasSubscriptionPermission } from "hooks/useHasSubscriptionPermission";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { useEffect, useState } from "react";
import { usePlaidLink } from "react-plaid-link";
import {
  useLazyGetGenerateLinkTokenQuery,
  useUpdateItemErrorStateMutation,
} from "store/apis/bankConnections";
import { Connection } from "store/apis/booksConnections";
import { BackendError } from "types/utils/error";

const PlaidReconnectDropdown = ({
  entityId,
  groupId,
  connectionId,
  isUpdating,
  custom_type,
  buttonTxt,
  onSuccess,
}: {
  entityId: string;
  groupId: string;
  connectionId: string;
  isUpdating: boolean;
  custom_type?: ButtonProps["customType"];
  buttonTxt: string;
  onSuccess: (publicToken: string) => void;
}) => {
  const { trackEvent } = useAnalytics();

  const [linkToken, setLinkToken] = useState("");
  const [isFetchingToken, setIsFetchingToken] = useState(false);

  const [fetchLinkToken] = useLazyGetGenerateLinkTokenQuery();

  const { open, ready } = usePlaidLink({
    token: linkToken,
    env: process.env.PUBLIC_PLAID_ENV,
    onSuccess: (publicToken, metadata) => {
      // @ts-ignore
      trackEvent(RECONNECT_PLAID_ACCOUNT_SUCCESS, {
        screen: "Bookkeeping - Plaid",
        account_name: metadata.institution?.name,
        institution_id: metadata.institution?.institution_id,
      });
      onSuccess(publicToken);
      setLinkToken("");
    },
    onExit: () => setLinkToken(""),
  });

  useEffect(() => {
    if (ready) open();
  }, [ready]);

  const handleSelect = async (isReconnect: boolean) => {
    if (document.body) document.body.style.pointerEvents = "auto";
    setIsFetchingToken(true);
    try {
      const result = await fetchLinkToken({
        groupId,
        entityItemId: connectionId,
        entityId,
        isReconnect: isReconnect || undefined,
      }).unwrap();
      setLinkToken(result.link_token);
    } finally {
      setIsFetchingToken(false);
    }
  };

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Dropdown.Root>
        <Dropdown.Trigger asChild>
          <Button
            customType={custom_type ?? "primary_outlined"}
            size="small"
            isLoading={isUpdating || isFetchingToken}
            disabled={isUpdating || isFetchingToken}
          >
            <span className="t-flex t-items-center t-gap-1.5">
              {buttonTxt}
              <CaretDown />
            </span>
          </Button>
        </Dropdown.Trigger>
        <Dropdown.Content align="start">
          <Dropdown.Label>Login & reconnect using</Dropdown.Label>
          <Dropdown.Item onSelect={() => handleSelect(false)}>
            Primary account credentials
          </Dropdown.Item>
          <Dropdown.Item onSelect={() => handleSelect(true)}>
            Other authorized credentials
          </Dropdown.Item>
        </Dropdown.Content>
      </Dropdown.Root>
    </div>
  );
};

export const ReConnect = ({
  onConnect,
  connection,
  isLoading,
  type = "RECONNECT",
  custom_type,
}: {
  connection: Connection & { direct_base_connection_id: string };
  onConnect: ReturnType<typeof useBankConnect>["onConnect"];
  isLoading: boolean;
  type?: "RECONNECT" | "UPDATE";
  custom_type?: ButtonProps["customType"];
}) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const { isCpa, isForeignCA } = useRoleBasedView();
  const { alertToast } = useToast();

  const [reconnectBankId, setReconnectBankId] = useState("");

  const [updateErrorItem, { isLoading: isUpdateItemErrorStateLoading }] =
    useUpdateItemErrorStateMutation();

  const permission = useHasSubscriptionPermission({
    feature: RECONNECT_BANK_ACCOUNT,
  });

  const permitted = !permission?.blocked && !permission?.isLoading;

  const buttonTxt = type === "RECONNECT" ? "Reconnect" : "Update";

  if (!permitted) {
    return (
      <PermissionBasedUI
        align="end"
        feature={RECONNECT_BANK_ACCOUNT}
        blockedUI={
          <BlockedButton
            size="small"
            customType={custom_type ? custom_type : "primary_outlined"}
          >
            {buttonTxt}
          </BlockedButton>
        }
      >
        <Button
          customType={custom_type ? custom_type : "primary_outlined"}
          size="small"
        >
          {buttonTxt}
        </Button>
      </PermissionBasedUI>
    );
  }

  if (connection.connection_type === "PLAID") {
    const connectionId = connection.uuid;
    const isUpdating =
      isUpdateItemErrorStateLoading && reconnectBankId === connectionId;

    return (
      <PlaidReconnectDropdown
        entityId={entityId}
        groupId={groupId}
        connectionId={connectionId}
        isUpdating={isUpdating}
        custom_type={custom_type}
        buttonTxt={buttonTxt}
        onSuccess={async (publicToken) => {
          setReconnectBankId(connectionId);
          try {
            await updateErrorItem({
              groupId,
              entityItemId: connectionId,
              publicToken,
            }).unwrap();
          } catch (error) {
            alertToast({
              message: (error as BackendError).data?.error?.message,
            });
          }
        }}
      />
    );
  }

  return (
    <Button
      customType={custom_type ? custom_type : "primary_outlined"}
      size="small"
      isLoading={isLoading}
      disabled={isLoading}
      onClick={(e) => {
        e.stopPropagation();
        onConnect({
          connectionId: connection.direct_base_connection_id,
          invokedFrom:
            isCpa || isForeignCA
              ? `/books/data-sources?company=${groupId}&entity=${entityId}`
              : `/books/data-sources?entity=${entityId}`,
          entityItemId: connection.uuid,
        });
      }}
    >
      {buttonTxt}
    </Button>
  );
};
